import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Search, Pencil, Trash2, ChevronLeft, ChevronRight, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { GuestContact } from '../../components/GuestDetails';
import { API } from '../../utils/api';
import { useAuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 50;

const PAYMENT_METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'SWISH', label: 'Swish' },
  { value: 'CASH', label: 'Cash' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'CARD', label: 'Card' },
  { value: 'OTHER', label: 'Other' },
];

export function BusinessTransactions({ initialSubAccountFilter }) {
  const { t } = useTranslation();
  const { headers, user } = useAuthContext();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('all');
  const [entryType, setEntryType] = useState('all');
  const [subAccounts, setSubAccounts] = useState([]);
  const [subAccountFilter, setSubAccountFilter] = useState(initialSubAccountFilter || 'all');
  const [page, setPage] = useState(1);

  // Edit manual transaction
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/business/sub-accounts`, { headers }).then(r => setSubAccounts(r.data.items || [])).catch(() => {});
    axios.get(`${API}/business/settings/categories`, { headers }).then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, [headers]);

  useEffect(() => {
    if (initialSubAccountFilter) setSubAccountFilter(initialSubAccountFilter);
  }, [initialSubAccountFilter]);

  const fetchTransactions = () => {
    setLoading(true);
    let url = `${API}/business/transactions?limit=${PAGE_SIZE}&skip=${(page - 1) * PAGE_SIZE}`;
    if (direction !== 'all') url += `&direction=${direction}`;
    if (subAccountFilter !== 'all') url += `&sub_account_id=${subAccountFilter}`;
    if (entryType !== 'all') url += `&entry_type=${entryType}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    axios.get(url, { headers })
      .then(res => { setTransactions(res.data.items || []); setTotal(res.data.total || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [direction, subAccountFilter, search, entryType]);

  useEffect(() => {
    const debounce = setTimeout(fetchTransactions, 300);
    return () => clearTimeout(debounce);
  }, [headers, direction, subAccountFilter, search, entryType, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleExport = async () => {
    try {
      let url = `${API}/business/transactions/export?format=csv`;
      if (subAccountFilter !== 'all') url += `&sub_account_id=${subAccountFilter}`;
      const res = await axios.get(url, { headers });
      const blob = new Blob([res.data.csv_data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `business_transactions_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (txn) => {
    setEditForm({
      id: txn.id,
      recipient_name: txn.recipient_name || '',
      amount: txn.amount,
      date: txn.date || '',
      payment_method: txn.payment_method || 'OTHER',
      category: txn.category || '',
      invoice_ref: txn.invoice_ref || '',
      sub_account_id: txn.sub_account_id || '',
      description: txn.description || '',
      notes: txn.notes || '',
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editForm) return;
    try {
      setEditSubmitting(true);
      await axios.put(`${API}/business/manual-transaction/${editForm.id}`, {
        recipient_name: editForm.recipient_name,
        amount: parseFloat(editForm.amount),
        date: editForm.date,
        payment_method: editForm.payment_method,
        category: editForm.category || undefined,
        invoice_ref: editForm.invoice_ref || undefined,
        sub_account_id: editForm.sub_account_id || undefined,
        description: editForm.description || undefined,
        notes: editForm.notes || undefined,
      }, { headers });
      toast.success('Transaction updated');
      setEditOpen(false);
      fetchTransactions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setEditSubmitting(false); }
  };

  const handleDelete = async (txnId) => {
    if (!confirm('Delete this manual transaction? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/business/manual-transaction/${txnId}`, { headers });
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const formatDateTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso || ''; }
  };

  const selectedSa = subAccountFilter !== 'all'
    ? subAccounts.find(sa => (sa.id || sa._id) === subAccountFilter)
    : null;
  const selectedSaName = selectedSa?.name || '';

  const fmtSEK = (n) => `${Number(n || 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} SEK`;

  return (
    <div className="space-y-4" data-testid="business-transactions">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Transactions ({total})</h2>
          {selectedSaName && <p className="text-xs text-muted-foreground">Filtered: {selectedSaName}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-1" /> CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-input" />
        </div>
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger className="w-28" data-testid="direction-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
          </SelectContent>
        </Select>
        <Select value={entryType} onValueChange={setEntryType}>
          <SelectTrigger className="w-28" data-testid="entry-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="SWISH">Swish</SelectItem>
            <SelectItem value="MANUAL">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={subAccountFilter} onValueChange={setSubAccountFilter}>
          <SelectTrigger className="w-44" data-testid="sub-account-filter">
            <SelectValue placeholder="Sub-account" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Accounts</SelectItem>
            {subAccounts.map(sa => (
              <SelectItem key={sa._id || sa.id} value={sa._id || sa.id}>{sa.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sub-account running totals (visible when a sub-account filter is active) */}
      {selectedSa && (
        <div className="grid grid-cols-3 gap-2" data-testid="sub-account-totals">
          <div className="rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">In</p>
            <p className="text-sm font-semibold text-emerald-700">{fmtSEK(selectedSa.total_received)}</p>
          </div>
          <div className="rounded-lg border bg-rose-50 border-rose-200 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-rose-700 font-medium">Out</p>
            <p className="text-sm font-semibold text-rose-700">{fmtSEK(selectedSa.total_sent)}</p>
          </div>
          <div className="rounded-lg border bg-slate-50 border-slate-200 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-slate-600 font-medium">Net</p>
            <p className={`text-sm font-semibold ${Number(selectedSa.balance || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtSEK(selectedSa.balance)}</p>
          </div>
        </div>
      )}

      {/* Transaction list */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : transactions.length === 0 ? (
        <Card><CardContent className="text-center py-8 text-muted-foreground" data-testid="no-transactions">No transactions found</CardContent></Card>
      ) : (
        <>
        {/* Mobile: stacked cards (no horizontal scrolling) */}
        <div className="md:hidden space-y-2" data-testid="transactions-cards">
          {transactions.map((txn, i) => {
            const isManual = txn.entry_type === 'MANUAL';
            const isSent = isManual || txn.from_user_id === user?.id;
            const displayName = isManual ? txn.recipient_name : (isSent ? txn.to_name : txn.from_name) || 'Unknown';
            const displayDate = isManual ? txn.date : txn.created_at;
            return (
              <Card key={txn.id || txn.trans_id || i} data-testid={`txn-card-${i}`}>
                <CardContent className="py-3 px-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full flex-shrink-0 ${isSent ? 'bg-rose-100' : 'bg-emerald-100'}`}>
                      {isSent ? <ArrowUpRight className="h-4 w-4 text-rose-600" /> : <ArrowDownLeft className="h-4 w-4 text-emerald-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{displayName}</p>
                          <p className="text-[11px] text-muted-foreground">{formatDateTime(displayDate)}</p>
                        </div>
                        <p className={`font-semibold text-sm whitespace-nowrap ${isSent ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {isSent ? '-' : '+'}{txn.amount} SEK
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {isManual
                          ? <Badge variant="secondary" className="text-[10px]">Manual</Badge>
                          : <Badge variant="outline" className="text-[10px]">Swish</Badge>}
                        {txn.sub_account_name && <Badge variant="outline" className="text-[10px]">{txn.sub_account_name}</Badge>}
                        {txn.description && <span className="text-[11px] text-muted-foreground truncate max-w-[150px]">{txn.description}</span>}
                        {isManual && (
                          <span className="ml-auto flex gap-1">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEdit(txn)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-500" onClick={() => handleDelete(txn.id)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block overflow-x-auto border rounded-lg" data-testid="transactions-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2.5 font-medium text-xs text-muted-foreground">Date</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs text-muted-foreground">Name</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs text-muted-foreground">{t('business.swishMessage')}</th>
                <th className="text-left px-3 py-2.5 font-medium text-xs text-muted-foreground">Sub-Account</th>
                <th className="text-right px-3 py-2.5 font-medium text-xs text-muted-foreground">Amount</th>
                <th className="text-center px-3 py-2.5 font-medium text-xs text-muted-foreground">Type</th>
                <th className="text-center px-3 py-2.5 font-medium text-xs text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((txn, i) => {
                const isManual = txn.entry_type === 'MANUAL';
                const isSent = isManual || txn.from_user_id === user?.id;
                const displayName = isManual ? txn.recipient_name : (isSent ? txn.to_name : txn.from_name) || 'Unknown';
                const displayDate = isManual ? txn.date : txn.created_at;
                return (
                  <tr key={txn.id || txn.trans_id || i} className="border-b last:border-0 hover:bg-gray-50" data-testid={`txn-row-${i}`}>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(displayDate)}</td>
                    <td className="px-3 py-2.5">
                      <span className="font-medium">{displayName}</span>
                      {!isManual && <GuestContact txn={txn} testId={`txn-guest-${i}`} />}
                      {isManual && txn.invoice_ref && (
                        <span className="block text-[10px] text-muted-foreground font-mono">{txn.invoice_ref}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-[200px] truncate">{txn.description || '-'}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {txn.sub_account_name ? (
                        <Badge variant="outline" className="text-[10px]">{txn.sub_account_name}</Badge>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-semibold ${isSent ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {isSent ? '-' : '+'}{txn.amount} SEK
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {isManual ? (
                        <Badge variant="secondary" className="text-[10px]">Manual</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">Swish</Badge>
                      )}
                      {isManual && txn.payment_method && (
                        <span className="block text-[9px] text-muted-foreground">{txn.payment_method.replace('_', ' ')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {isManual ? (
                        <div className="flex gap-1 justify-center">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEdit(txn)} data-testid={`edit-txn-${i}`}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-500 hover:text-rose-700" onClick={() => handleDelete(txn.id)} data-testid={`delete-txn-${i}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : <span className="text-muted-foreground text-xs">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2" data-testid="pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="page-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        </>
      )}

      {/* Edit Manual Transaction Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
            <DialogDescription>Update this manual transaction entry.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>Recipient Name</Label>
                <Input value={editForm.recipient_name} onChange={e => setEditForm(p => ({ ...p, recipient_name: e.target.value }))} data-testid="edit-recipient" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Amount (SEK)</Label>
                  <Input type="number" value={editForm.amount} onChange={e => setEditForm(p => ({ ...p, amount: e.target.value }))} data-testid="edit-amount" />
                </div>
                <div className="space-y-1">
                  <Label>Date</Label>
                  <Input type="date" value={editForm.date} onChange={e => setEditForm(p => ({ ...p, date: e.target.value }))} data-testid="edit-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Payment Method</Label>
                  <Select value={editForm.payment_method} onValueChange={v => setEditForm(p => ({ ...p, payment_method: v }))}>
                    <SelectTrigger data-testid="edit-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select value={editForm.category} onValueChange={v => setEditForm(p => ({ ...p, category: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="edit-category"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Invoice Reference</Label>
                <Input value={editForm.invoice_ref} onChange={e => setEditForm(p => ({ ...p, invoice_ref: e.target.value }))} data-testid="edit-invoice" />
              </div>
              <div className="space-y-1">
                <Label>Sub-Account</Label>
                <Select value={editForm.sub_account_id || 'none'} onValueChange={v => setEditForm(p => ({ ...p, sub_account_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="edit-sub-account"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No allocation</SelectItem>
                    {subAccounts.map(sa => <SelectItem key={sa.id || sa._id} value={sa.id || sa._id}>{sa.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Input value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} data-testid="edit-description" />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea rows={2} value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} data-testid="edit-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} disabled={editSubmitting} data-testid="edit-save-btn">
              {editSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
